import { useRef, useState } from 'react';
import {
  TICKET_PRIORITIES,
  type TicketPriority,
  type TicketStaffView,
} from '@traceio-org/platform-contracts';
import { AdminProblem } from '../../components/AdminProblem';
import { SelectField, TextAreaField, TextField } from '../../components/fields';
import { WriteDialog } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { useAdminRead } from '../../useAdminRead';
import { PRIORITY_LABELS } from './ticketFormat';

interface Form {
  organizationId: string;
  memberQuery: string;
  requesterUserId: string;
  title: string;
  body: string;
  priority: TicketPriority;
}

type Errors = Partial<Record<'organizationId' | 'requesterUserId' | 'title' | 'body', string>>;

/** The contract's bounds (staffTicketCreateRequestSchema): a title of 3 to 140 characters, details up to 10,000. */
const TITLE_MIN = 3;
const TITLE_MAX = 140;
const BODY_MAX = 10_000;

/** The organizations a staff member files for: the first page of the list (search narrows it). */
function OrganizationChoice({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (id: string) => void;
  error: string | null;
}) {
  const { api } = useAdmin();
  const read = useAdminRead('ticket-organizations', (signal) =>
    // platform-api's largest page; a customer past it is filed from its own organization page.
    api.listOrganizations({ limit: 100 }, signal),
  );
  if (read.failure)
    return <AdminProblem failure={read.failure} subject="the organizations" onRetry={read.retry} />;
  const options = (read.data?.items ?? []).map((org) => ({ value: org.id, label: org.displayName }));
  return (
    <SelectField
      label="Customer"
      value={value}
      onChange={onChange}
      options={[{ value: '', label: read.data ? 'Choose an organization' : 'Loading…' }, ...options]}
      error={error}
      required
    />
  );
}

/** The person the ticket is for: an active member of the organization, who receives the replies. */
function RequesterChoice({
  organizationId,
  query,
  onQuery,
  value,
  onChange,
  error,
}: {
  organizationId: string;
  query: string;
  onQuery: (query: string) => void;
  value: string;
  onChange: (userId: string) => void;
  error: string | null;
}) {
  const { api } = useAdmin();
  const key = organizationId ? `ticket-members\u0000${organizationId}\u0000${query.trim()}` : null;
  const read = useAdminRead(key, (signal) =>
    api.listMembers(organizationId, { status: 'active', ...(query.trim() ? { query } : {}) }, signal),
  );
  const members = read.dataKey === key ? (read.data?.items ?? []) : [];
  return (
    <>
      <TextField
        label="Find the person"
        type="search"
        value={query}
        onChange={onQuery}
        hint="Their name or email. They receive Trace's replies."
        spellCheck={false}
      />
      {read.failure ? (
        <AdminProblem failure={read.failure} subject="the organization's members" onRetry={read.retry} />
      ) : (
        <SelectField
          label="Requester"
          value={value}
          onChange={onChange}
          options={[
            {
              value: '',
              label: read.loading ? 'Loading…' : members.length ? 'Choose a person' : 'Nobody found',
            },
            ...members.map((m) => ({ value: m.userId, label: `${m.displayName} (${m.email})` })),
          ]}
          error={error}
          required
        />
      )}
    </>
  );
}

/**
 * Staff file a ticket for a customer, for example after a phone call (POST /v1/admin/tickets): it starts in Todo,
 * already with Trace, and the customer's help desk sees it. From an organization's Tickets tab the customer is fixed.
 */
export function NewTicketDialog({
  open,
  onClose,
  onCreated,
  organizationId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (ticket: TicketStaffView) => void;
  /** The customer, when filed from its organization page. */
  organizationId?: string;
}) {
  const { api } = useAdmin();
  const [form, setForm] = useState<Form>({
    organizationId: organizationId ?? '',
    memberQuery: '',
    requesterUserId: '',
    title: '',
    body: '',
    priority: 'none',
  });
  const [errors, setErrors] = useState<Errors>({});
  // The answer is read in onSaved, right after onSubmit resolves (a state update would not be visible yet).
  const created = useRef<TicketStaffView | null>(null);
  const set = <K extends keyof Form>(field: K, value: Form[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title="New ticket"
      description="For a customer, for example after a call. It starts in Todo, and their help desk sees it."
      submitLabel="Create ticket"
      onSubmit={async () => {
        const found: Errors = {};
        if (!form.organizationId) found.organizationId = 'Choose the customer.';
        if (!form.requesterUserId) found.requesterUserId = 'Choose who the ticket is for.';
        const title = form.title.trim();
        if (title.length < TITLE_MIN) found.title = `Write a title of at least ${TITLE_MIN} characters.`;
        else if (title.length > TITLE_MAX) found.title = `Write at most ${TITLE_MAX} characters.`;
        if (!form.body.trim()) found.body = "Describe what's wrong.";
        setErrors(found);
        if (Object.keys(found).length > 0) return 'invalid';
        const ticket = await api.createTicket({
          organizationId: form.organizationId,
          requesterUserId: form.requesterUserId,
          title,
          body: form.body,
          priority: form.priority,
        });
        created.current = ticket;
        return undefined;
      }}
      onSaved={() => {
        if (created.current) onCreated(created.current);
      }}
    >
      {organizationId ? null : (
        <OrganizationChoice
          value={form.organizationId}
          onChange={(value) => {
            set('organizationId', value);
            set('requesterUserId', '');
          }}
          error={errors.organizationId ?? null}
        />
      )}
      {form.organizationId ? (
        <RequesterChoice
          organizationId={form.organizationId}
          query={form.memberQuery}
          onQuery={(value) => set('memberQuery', value)}
          value={form.requesterUserId}
          onChange={(value) => set('requesterUserId', value)}
          error={errors.requesterUserId ?? null}
        />
      ) : null}
      <TextField
        label="Title"
        value={form.title}
        onChange={(value) => set('title', value)}
        error={errors.title ?? null}
        maxLength={TITLE_MAX}
        required
      />
      <TextAreaField
        label="What's wrong"
        value={form.body}
        onChange={(value) => set('body', value)}
        error={errors.body ?? null}
        maxLength={BODY_MAX}
        rows={5}
        required
      />
      <SelectField
        label="Priority"
        value={form.priority}
        onChange={(value) => set('priority', value as TicketPriority)}
        options={TICKET_PRIORITIES.map((value) => ({ value, label: PRIORITY_LABELS[value] }))}
      />
    </WriteDialog>
  );
}
